<template>
  <div class="my-tasks-tree-table">
    <component :is="'style'" v-html="dynamicTreeStyles"></component>
    <thux-table
      :instance="instance"
      :instance-name="instanceName"
      :order-by-dict="orderByDict"
      :goBack="goBack"
      :list="displayList"
      :fieldName="fieldName"
      :fields="visibleFields"
      :fields-column="fieldsColumn"
      :select-all="selectAll"
      :selected-list="selectedList"
      :get-local-selected-all="getLocalSelectedAll"
      :local-selected-list="localSelectedList"
      :tbody-tr-class="tbodyTrClassFn"
      @check-all="checkAll"
      @check-none="checkNone"
      @set-selected-row="setSelectedRow"
      :show-checks="showChecks"
      :showEditButton="showEditButton"
      :showDetailButton="showDetailButton"
      @change-status="changeInstanceStatus"
      @sort-changed="orderTable"
      @remove-order-by="removeOrderFromTable"
      @open-detail-form="$emit('open-detail-form', $event)"
    >
      <template slot="expander" slot-scope="row">
        <button
          v-if="canExpand(row.item)"
          type="button"
          class="task-tree-toggle"
          :class="{ 'task-tree-toggle-nested': row.item._level > 0 }"
          :title="isExpanded(row.item) ? $t('Collapse row') : $t('Expand row')"
          @click.stop="toggleRow(row.item)"
        >
          <i v-if="isLoading(row.item)" class="fas fa-spinner fa-spin" />
          <template v-else-if="row.item._level > 0">
            <i v-if="isExpanded(row.item)" class="fas fa-minus-square" />
            <i v-else class="fas fa-plus-square" />
          </template>
          <template v-else>
            <i v-if="isExpanded(row.item)" class="fas fa-caret-down" />
            <i v-else class="fas fa-caret-right" />
          </template>
        </button>
        <i
          v-else-if="row.item._level > 0"
          class="fas fa-level-down-alt task-tree-child-hint"
          :title="$t('Sub-task')"
          aria-hidden="true"
        />
      </template>
      <template slot="quotation_link__quotation__code" slot-scope="row">
        <span>{{ quotationLabel(row.item) }}</span>
      </template>
      <template slot="tags__name" slot-scope="row">
        <span
          v-for="tag in (row.item.tags_detail || [])"
          :key="tag.id"
          class="badge task-tag-chip mr-1"
          :style="tag.color ? { backgroundColor: tag.color, color: '#fff' } : {}"
        >
          {{ tag.name }}
        </span>
      </template>
      <template slot="priority__name" slot-scope="row">
        <span
          class="badge task-priority-chip"
          :style="{ backgroundColor: row.item.priority_color || '#6c757d', color: '#fff' }"
        >
          {{ row.item.priority_name }}
        </span>
      </template>
      <template slot="state__name" slot-scope="row">
        <span
          class="badge task-state-chip"
          :style="{ backgroundColor: row.item.state_color || '#6c757d', color: '#fff' }"
        >
          {{ row.item.state_name }}
        </span>
      </template>
      <template slot="due_date" slot-scope="row">
        <span :class="{ 'text-red': isExpired(row.item) }">
          {{ row.item.due_date | date }}
        </span>
      </template>
      <template slot="comments_count" slot-scope="row">
        <div class="task-comments-cell">
          <button
            type="button"
            class="task-comments-cell__pill"
            :class="{ 'task-comments-cell__pill--empty': !row.item.comments_count }"
            :title="$t('Open comments')"
            @click.stop="openComments(row.item)"
          >
            <i class="fas fa-comment-dots" />{{ row.item.comments_count || 0 }}
          </button>
        </div>
      </template>
      <template slot="other-buttons-actions-top" slot-scope="row">
        <b-btn
          v-if="!row.item.parent"
          variant="outline-primary"
          size="sm"
          class="mr-1"
          :title="$t('Create subtask')"
          @click.stop="onCreateSubtaskClick(row.item)"
        >
          <i class="fas fa-sitemap"></i>
        </b-btn>
        <b-btn
          v-if="canTerminate(row.item)"
          variant="outline-success"
          size="sm"
          class="mr-1"
          :title="$t('Terminate task')"
          @click.stop="onTerminateClick(row.item)"
        >
          <i class="fas fa-flag-checkered"></i>
        </b-btn>
      </template>
      <template slot="other-buttons-actions-bottom" slot-scope="row">
        <b-btn
          v-if="canTerminate(row.item)"
          variant="outline-success"
          size="sm"
          class="mr-1"
          :title="$t('Delete task')"
          @click.stop="onDeleteClick(row.item)"
        >
          <i class="fas fa-trash"></i>
        </b-btn>
      </template>
    </thux-table>
    <task-comment-modal
      v-if="taskForComments"
      :task="taskForComments"
      @close="onCommentsClose"
    />
    <task-terminate-modal
      v-if="taskToTerminate"
      :task="taskToTerminate"
      @terminated="onTerminated"
      @close="taskToTerminate = null"
    />
    <task-open-subtasks-modal
      v-if="taskWithOpenSubtasks"
      :task-id="taskWithOpenSubtasks.id"
      @subtask-closed="onSubtaskClosed"
      @all-subtasks-closed="onOpenSubtasksAllClosed"
      @close="taskWithOpenSubtasks = null"
    />
    <task-delete-modal
      v-if="taskToDelete"
      :task="taskToDelete"
      :subtasks="(childrenMap[taskToDelete.id] || {}).items || []"
      @confirm="onDeleteConfirm"
      @close="taskToDelete = null"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { TYPES as MY_INTERNAL_TASKS_TYPES } from './store'
import { TYPES as TASK_TYPES } from '@/pages/task/task/store'

import ThuxTableMixin from '../../../components/thux-table/ThuxTableMixin'
import ThuxTableChecksMixin from '../../../components/thux-table/thux-table-checks/ThuxTableChecksMixin'
import TaskCommentModal from '@/pages/task/task/components/TaskCommentModal.vue'
import TaskTerminateModal from '@/pages/task/task/components/TaskTerminateModal.vue'
import TaskOpenSubtasksModal from '@/pages/task/task/components/TaskOpenSubtasksModal.vue'
import TaskDeleteModal from '@/pages/task/task/components/TaskDeleteModal.vue'
import { axiosThux } from '@/axiosThux'

const DESCRIPTION_INDENT_BUDGET_PX = 30

export default {
  name: 'MyInternalTasksTable',
  mixins: [ThuxTableMixin, ThuxTableChecksMixin],
  components: {
    'task-comment-modal': TaskCommentModal,
    'task-terminate-modal': TaskTerminateModal,
    'task-open-subtasks-modal': TaskOpenSubtasksModal,
    'task-delete-modal': TaskDeleteModal
  },
  data () {
    return {
      expandedSet: [],
      childrenMap: {},
      taskForComments: null,
      taskToTerminate: null,
      taskWithOpenSubtasks: null,
      taskToDelete: null,
      fields: [
        {
          key: 'expander',
          label: '',
          sortable: false,
          custom: true,
          thStyle: { width: '3%' },
          thClass: 'task-tree-expander-col',
          tdClass: 'task-tree-expander-col task-tree-shiftable'
        },
        {
          key: 'category__name',
          label: this.$t('Category'),
          tdClass: 'text-nowrap',
          thClass: 'text-nowrap',
          sortable: true,
          value: 'category_name'
        },
        {
          key: 'name',
          label: this.$t('Activity'),
          tdClass: 'text-wrap',
          thClass: 'text-nowrap',
          sortable: true,
          value: 'name'
        },
        {
          key: 'quotation_link__quotation__code',
          label: this.$t('Quotation'),
          tdClass: 'text-nowrap',
          thClass: 'text-nowrap',
          sortable: true,
          custom: true
        },
        {
          key: 'tags__name',
          label: this.$t('Tags'),
          tdClass: 'text-wrap',
          thClass: 'text-nowrap',
          sortable: true,
          custom: true
        },
        {
          key: 'priority__name',
          label: this.$t('Priority'),
          tdClass: 'text-nowrap',
          thClass: 'text-nowrap',
          sortable: true,
          custom: true
        },
        {
          key: 'state__name',
          label: this.$t('State'),
          tdClass: 'text-nowrap',
          thClass: 'text-nowrap',
          sortable: true,
          custom: true
        },
        {
          key: 'due_date',
          label: this.$t('Due date'),
          tdClass: 'text-nowrap',
          thClass: 'text-nowrap',
          sortable: true,
          custom: true
        },
        {
          key: 'assignee_names',
          label: this.$t('Assignees'),
          tdClass: 'text-wrap text-uppercase',
          thClass: 'text-nowrap',
          sortable: false,
          value: 'assignee_names'
        },
        {
          key: 'comments_count',
          label: this.$t('Comments'),
          tdClass: 'text-center text-nowrap',
          thClass: 'text-center text-nowrap',
          sortable: false,
          custom: true,
          thStyle: { width: '6%' }
        },
        {
          key: 'action',
          label: this.$t('Actions'),
          tdClass: 'text-right text-nowrap',
          thClass: 'text-right text-nowrap',
          thStyle: { width: '7%' }
        }
      ],
      fieldsColumn: [],
      fieldName: 'id'
    }
  },
  watch: {
    // Al ricaricamento della lista (es. dopo aver aggiunto una sotto-attività da
    // TaskEdit) NON collassiamo gli alberi aperti: manteniamo lo stato espanso e
    // ri-carichiamo i figli dei rami attualmente aperti. Così un padre già
    // espanso mostra subito la nuova sotto-attività (anche la seconda, la terza,
    // ...) senza dover richiudere e riaprire la riga a mano.
    list () {
      const expanded = this.expandedSet.slice()
      this.$set(this, 'childrenMap', {})
      expanded.forEach((id) => { this.fetchChildren({ id }) })
    }
  },
  computed: {
    ...mapGetters({
      selectAll: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.selectAll,
      selectedList: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.selectedList,
      maxTreeDepth: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.maxTreeDepth,
      summaryFilter: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.summaryFilter,
      authProfile: 'auth/profile'
    }),
    profile () {
      return this.authProfile || 'generic'
    },
    isReadOnly () {
      return !!(this.params && this.params.readOnly)
    },
    visibleFields () {
      if (!this.isReadOnly) return this.fields
      return this.fields.filter((f) => f.key !== 'assignee_names')
    },
    showChecks () { return false },
    showDetailButton () { return true },
    showEditButton () { return false },
    rootItems () {
      return Array.isArray(this.list) ? this.list : []
    },
    shiftPerLevel () {
      if (this.maxTreeDepth <= 0) return 0
      return DESCRIPTION_INDENT_BUDGET_PX / this.maxTreeDepth
    },
    dynamicTreeStyles () {
      if (this.shiftPerLevel <= 0) return ''
      let css = ''
      for (let level = 1; level <= this.maxTreeDepth; level++) {
        const shift = Math.round(level * this.shiftPerLevel)
        css += `.my-tasks-tree-table tr.task-tree-row-level-${level} > td.task-tree-shiftable{transform:translateX(${shift}px);}`
      }
      return css
    },
    displayList () {
      const out = []
      const pushNode = (node, level, rootIndex) => {
        out.push(Object.assign({}, node, { _level: level, _rootIndex: rootIndex }))
        if (this.expandedSet.indexOf(node.id) === -1) return
        const entry = this.childrenMap[node.id]
        if (!entry || !entry.loaded) return
        entry.items.forEach((child) => pushNode(child, level + 1, rootIndex))
      }
      this.rootItems.forEach((item, i) => pushNode(item, 0, i))
      return out
    }
  },
  methods: {
    ...mapMutations({
      setList: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.MUTATIONS.setList,
      setSelectedList: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.MUTATIONS.setSelectedList,
      setSelectAll: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.MUTATIONS.setSelectAll
    }),
    ...mapActions({
      ...MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.ACTIONS,
      fetchMaxTreeDepth: MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.ACTIONS.fetchMaxTreeDepth,
      markDone: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.markDone,
      setStatus: TASK_TYPES.GENERIC.task.task.DETAIL.ACTIONS.setStatus
    }),
    quotationLabel (item) {
      const q = item && item.quotation_detail
      if (!q) return ''
      return q.description ? `${q.quotation_code} (${q.description})` : (q.quotation_code || '')
    },
    canTerminate (item) {
      return !!item && !item.state_is_final
    },
    onDeleteClick (item) {
      this.taskToDelete = item
    },
    onDeleteConfirm (item) {
      this.setStatus({ id: item.id, status: 0 }).then(() => {
        this.refreshAfterTerminate(item)
      })
    },
    async onTerminateClick (item) {
      if (item.has_open_subtasks) {
        this.taskWithOpenSubtasks = item
        return
      }
      await this.proceedTerminate(item)
    },
    async proceedTerminate (item) {
      const quotationId = item.quotation || (item.quotation_detail && item.quotation_detail.id)
      if (quotationId) {
        this.taskToTerminate = Object.assign({}, item, { quotation: quotationId })
      } else {
        try {
          await this.markDone({ id: item.id })
          this.refreshAfterTerminate(item)
        } catch (e) {
          // errore già notificato da handleError
        }
      }
    },
    onSubtaskClosed () {
      // Una sotto-attività è stata chiusa dalla modale: aggiorna l'albero e i
      // contatori della dashboard.
      const item = this.taskWithOpenSubtasks
      if (item && this.childrenMap[item.id]) {
        this.fetchChildren({ id: item.id })
      }
      this.getList()
      this.$emit('task-done', item)
    },
    onOpenSubtasksAllClosed () {
      // Tutti i figli di primo livello sono chiusi: prosegue con la chiusura del
      // padre (preventivo/markDone).
      const item = this.taskWithOpenSubtasks
      this.taskWithOpenSubtasks = null
      if (item) this.proceedTerminate(item)
    },
    onTerminated () {
      const item = this.taskToTerminate
      this.taskToTerminate = null
      this.refreshAfterTerminate(item)
    },
    refreshAfterTerminate (item) {
      if (item && item.parent && this.childrenMap[item.parent]) {
        this.fetchChildren({ id: item.parent })
        this.getList()
      } else {
        this.getList()
      }
      // Notify the dashboard so the visible-task/summary/ counters get reloaded.
      this.$emit('task-done', item)
    },
    isExpired (item) {
      if (!item || !item.due_date || item.state_is_final) return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const due = new Date(item.due_date)
      return due < today
    },
    canExpand (item) {
      if (!item || !item.has_children) return false
      // When the list is filtered to "open" tasks, only show the expander if there is
      // at least one open subtask (otherwise expanding would show an empty/filtered tree).
      if (this.summaryFilter === 'open') return !!item.has_open_subtasks
      return true
    },
    isExpanded (item) {
      return this.expandedSet.indexOf(item.id) !== -1
    },
    isLoading (item) {
      const entry = this.childrenMap[item.id]
      return !!(entry && entry.loading)
    },
    toggleRow (item) {
      const id = item.id
      const idx = this.expandedSet.indexOf(id)
      if (idx !== -1) {
        this.expandedSet.splice(idx, 1)
        return
      }
      // Ricarica sempre i figli a ogni espansione: se una sottoattività è stata
      // aggiunta/modificata mentre la riga era chiusa, la riapertura mostra i dati
      // aggiornati invece della cache stale.
      this.fetchChildren(item).then((ok) => {
        if (ok) this.expandedSet.push(id)
      })
    },
    fetchChildren (item) {
      const id = item.id
      this.$set(this.childrenMap, id, { loading: true, loaded: false, error: false, items: [] })
      // status=1: le sotto-attività cancellate sono soft-deleted e non vanno
      // mostrate nell'albero.
      let url = `/api/v1/task/${this.profile}/visible-task/my-internal/?page_size=1000&status=1&parent=${id}`
      // Carry over the list's active filters so subtasks respect the same filtering.
      const summaryFilter = this.$store.getters[MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.summaryFilter]
      if (summaryFilter) {
        url = `${url}&summary_filter=${summaryFilter}`
      }
      if (this.$store.getters['GENERIC/dashboard/myinternaltasks/onlyMine']) {
        url = `${url}&only_mine=true`
      }
      const filters = this.$store.getters[MY_INTERNAL_TASKS_TYPES.GENERIC.dashboard.myinternaltasks.LIST.GETTERS.filters] || {}
      Object.keys(filters).forEach((key) => {
        if (filters[key] != null) {
          url = `${url}&${key}=${encodeURIComponent(filters[key])}`
        }
      })
      return axiosThux.get(url)
        .then((response) => {
          const items = (response.data && response.data.results) || []
          this.$set(this.childrenMap, id, { loading: false, loaded: true, error: false, items })
          return true
        })
        .catch((error) => {
          this.$set(this.childrenMap, id, { loading: false, loaded: false, error: true, items: [] })
          const payload = (error && error.response && error.response.data) || { detail: this.$t('Error loading children') }
          this.$store.dispatch('notifyError', { error: payload })
          return false
        })
    },
    tbodyTrClassFn (item) {
      if (!item) return null
      const classes = []
      const level = item._level || 0
      if (level > 0) classes.push(`task-tree-row-level-${level}`)
      const rootIdx = typeof item._rootIndex === 'number' ? item._rootIndex : 0
      classes.push(rootIdx % 2 === 0 ? 'task-tree-group-even' : 'task-tree-group-odd')
      return classes.join(' ')
    },
    onCreateSubtaskClick (item) {
      this.$emit('open-detail-form', item, true)
    },
    openComments (item) {
      this.taskForComments = item
    },
    onCommentsClose () {
      this.taskForComments = null
    }
  },
  mounted () {
    this.fetchMaxTreeDepth()
  }
}
</script>
